// Reception.js
import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './Reception.css';
import axiosInstance from '../../../Config/axiosInstance';
import ReceptionEdit from './EditReception';
import HistoryReception from './HistoryReception';
import NotAuthorized from '../../../Components/ProtectedRoute/NotAuthorized';

import { Box, Button, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import IconButton from '@mui/material/IconButton';

const getPathFromUrl = url => {
    try {
        const { pathname } = new URL(url);
        return pathname;
    } catch (e) {
        return url;
    }
}

const ReceptionList = ({ canEdit, canDelete, canViewHistory }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedReceptionId, setSelectedReceptionId] = useState(null);
    const [openEditDisabledDialog, setOpenEditDisabledDialog] = useState(false);
    const [editDisabledReason, setEditDisabledReason] = useState('');

    const formatDateToRomanian = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        return `${day}.${month}.${year}`;
    };

    useEffect(() => {
        const fetchReception = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get("/reception/");
                console.log("Date reception:", response.data);
                
                const receptions = response.data.sort((a, b) => b.id - a.id);
                
                // Verifică dacă există deja po_status în date
                if (receptions.length > 0 && receptions[0].po_status) {
                    console.log("Există deja po_status în date!");
                    setRows(receptions);
                    setLoading(false);
                    return;
                }
                
                // Dacă nu există, fă fetch separat pentru fiecare PO
                const receptionsWithPoStatus = await Promise.all(
                    receptions.map(async (reception) => {
                        try {
                            // Folosește ID-ul purchase_order
                            const poId = reception.purchase_order;
                            console.log(`Fetch PO for ID: ${poId}`);
                            
                            const poResponse = await axiosInstance.get(`/purchase-orders/${poId}/`);
                            console.log(`PO response for ${poId}:`, poResponse.data);
                            
                            return {
                                ...reception,
                                po_number: poResponse.data.order_number,
                                po_status: poResponse.data.status
                            };
                        } catch (error) {
                            console.error(`Eroare la PO ${reception.purchase_order}:`, error);
                            return {
                                ...reception,
                                po_number: reception.po_number || 'N/A',
                                po_status: 'N/A'
                            };
                        }
                    })
                );
                
                console.log("Final rows:", receptionsWithPoStatus);
                setRows(receptionsWithPoStatus);
            } catch (error) {
                console.error("Eroare la preluarea Receptiei", error);
            } finally {
                setLoading(false);
            }
        };

        fetchReception();
    }, []);

    const getStatusClass = (status) => {
        switch (status) {
            case 'NOT RECEIVED':
                return 'reception-status-badge reception-status-not-received';
            case 'PARTIALLY RECEIVED':
                return 'reception-status-badge reception-status-partially-received';
            case 'RECEIVED':
                return 'reception-status-badge reception-status-received';
            case 'CANCELED':
                return 'reception-status-badge reception-status-canceled';
            default:
                return 'reception-status-badge';
        }
    };

    const isValidPoStatusForEdit = (poStatus) => {
        return poStatus === 'ORDERED' || poStatus === 'PARTIALLY RECEIVED' || poStatus === 'RECEIVED';
    };

    const isEditAllowed = (receptionStatus, poStatus) => {
        if (receptionStatus === "CANCELED") {
            return false;
        }
        
        if (!poStatus || poStatus === 'N/A') {
            return false;
        }
        
        return isValidPoStatusForEdit(poStatus);
    };

    const getEditDisabledReason = (receptionStatus, poStatus) => {
        if (receptionStatus === "CANCELED") {
            return "Edit not allowed: Reception has been CANCELED. Canceled receptions cannot be modified.";
        }
        
        if (!poStatus || poStatus === 'N/A') {
            return "Edit not allowed: Purchase Order status is not available. Please ensure the Purchase Order exists and is accessible.";
        }
        
        if (poStatus === 'ORDERED') {
            return "Edit allowed: Purchase Order status is ORDERED.";
        }
        
        if (poStatus === 'PARTIALLY RECEIVED') {
            return "Edit allowed: Purchase Order status is PARTIALLY RECEIVED.";
        }
        
        if (poStatus === 'RECEIVED') {
            return "Edit allowed: Purchase Order status is RECEIVED.";
        }
        
        return `Edit not allowed: Purchase Order status is "${poStatus}".`;
    };

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/reception/${selectedReceptionId}/`);
            setRows(prev => prev.filter(r => r.id !== selectedReceptionId));
        } catch (error) {
            console.error("Eroare la stergerea receptiei: ", error);
        } finally {
            handleCloseDialog();
        }
    };

    const handleOpenDialog = (id) => {
        setSelectedReceptionId(id);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedReceptionId(null);
    };

    const handleViewHistory = (receptionId, poNumber) => {
        navigate(`/reception/history/${receptionId}?poNumber=${encodeURIComponent(poNumber)}`);
    };

    const handleDisabledEditClick = (reason) => {
        setEditDisabledReason(reason);
        setOpenEditDisabledDialog(true);
    };

    const columns = [
        {
            field: "po_number",
            headerName: "PO number",
            minWidth: 150,
            flex: 1,
            headerClassName: "bold-header",
            renderCell: (params) => params.value || 'N/A'
        },
        {
            field: "delivery_date",
            headerName: "Delivery date",
            minWidth: 150,
            flex: 1,
            headerClassName: "bold-header",
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {formatDateToRomanian(params.value)}
                </Box>
            )
        },
        {
            field: "status",
            headerName: "Status Reception",
            minWidth: 220,
            flex: 1,
            headerClassName: "bold-header",
            renderCell: (params) => (
                <Box>
                    <span className={getStatusClass(params.value)}>
                        {params.value || 'N/A'}
                    </span>
                </Box>
            ),
        },
        {
            field: "actions",
            headerName: "Action",
            flex: 1,
            minWidth: 180,
            headerClassName: "bold-header",
            headerAlign: "center",
            renderCell: (params) => {
                const editDisabled = !isEditAllowed(params.row.status, params.row.po_status);
                const disabledReason = getEditDisabledReason(params.row.status, params.row.po_status);
                
                return (
                    <div style={{ display: "flex", justifyContent: "center", gap: "6px", width: "100%" }}>
                        
                        {canViewHistory && (
                            <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleViewHistory(params.row.id, params.row.po_number)}
                                title="View reception history"
                            >
                                <HistoryIcon fontSize='small' />
                            </IconButton>
                        )}

                        {canEdit && (
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={editDisabled ? () => handleDisabledEditClick(disabledReason) : () => navigate(`/reception/edit/${params.row.id}`)}
                                sx={editDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                title={disabledReason}
                            >
                                <EditIcon fontSize='small' />
                            </IconButton>
                        )}

                        {canDelete && (
                            <IconButton
                                size='small'
                                color='error'
                                onClick={() => handleOpenDialog(params.row.id)}
                                title="Delete reception"
                            >
                                <DeleteIcon fontSize='small' />
                            </IconButton>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <div className='reception-page'>
            <div className='header-container'>
                <h2>RECEPTION</h2>
            </div>

            <Box className='reception-box-subcontainter reception-datagrid'>
                <div className='reception-datagrid-container'>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            getRowHeight={() => "auto"}
                            initialState={{
                                sorting: { sortModel: [{ field: "id", sort: "desc" }] },
                                pagination: { paginationModel: { pageSize: 12 } },
                            }}
                            disableRowSelectionOnClick
                            pageSizeOptions={[10, 12, 20, 25]}
                        />
                    )}
                </div>
            </Box>

            {/* Dialog pentru confirmarea ștergerii */}
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this reception?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog pentru explicarea motivului pentru care editarea nu este disponibilă */}
            <Dialog open={openEditDisabledDialog} onClose={() => setOpenEditDisabledDialog(false)}>
                <DialogTitle>Edit Not Available</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {editDisabledReason}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDisabledDialog(false)} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const Reception = () => {
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const hasPermission = (path, checkPermission) => {
        return permissions.some(
            (page) => getPathFromUrl(page.url) === path && checkPermission(page)
        );
    };

    const canEdit = hasPermission('/reception', (page) => page.can_edit);
    const canDelete = hasPermission('/reception', (page) => page.can_delete);
    const canViewHistory = hasPermission('/reception', (page) => page.can_view);

    return (
        <Routes>
            <Route path="/" element={<ReceptionList canEdit={canEdit} canDelete={canDelete} canViewHistory={canViewHistory} />} />
            <Route path="edit/:id" element={canEdit ? <ReceptionEdit /> : <NotAuthorized />} />
            <Route path="history/:id" element={canViewHistory ? <HistoryReception /> : <NotAuthorized/>} />
        </Routes>
    );
};

export default Reception;